import {
  EmrBed,
  EmrCheck,
  EmrCheckmarkShield,
  EmrDischarged,
  EmrDocument,
  EmrDottedRightArrow,
  EmrExclamationTriangle,
  EmrFolder,
  EmrHand,
  EmrHeartBolt,
  EmrKey,
  EmrLabs,
  EmrNurse,
  EmrPillsBottle,
  EmrPlus,
  EmrPrinter,
  EmrProfileCircle,
  EmrRadiology,
  EmrRemove,
  EmrTransportation,
  EmrVitals,
  SiChevronLeft,
  SiPencil,
} from "@medstonetech/slate-icons";

import * as React from "react";
import * as yup from "yup";

import {
  Box,
  BoxProps,
  CSSObject,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  Tab,
  TabPanel,
  TabPanels,
  Text,
  chakra,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  Button,
  Card,
  CheckboxListGroup,
  CheckboxListItem,
  DividerList,
  Loading,
  PdfViewer,
  PdfViewerActivePage,
  PdfViewerContainer,
  PdfViewerHeader,
  PdfViewerPreview,
  PdfViewerSmallPageController,
  PdfZoomControls,
  TabList,
  Tabs,
  Textarea,
  ToolbarHeader,
  WarningDialog,
} from "shared";
import { ChartRouteBaseParams } from "modules";
import {
  UseGenerateCustomSummaryDocumentPayload,
  UseSummaryMiscellaneous,
  useActiveCharts,
  useAddendums,
  useGenerateCustomSummaryDocument,
  useSignedSectionsFiles,
  useSummaryMiscellaneous,
  useSummaryProgress,
} from "modules/charts-shared/api";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import {
  DocumentItem,
  SectionItem,
  SectionItemRemove,
  SignedItem,
} from "./SectionListItem";

import { useEncounterInfo } from "modules/reception/api";
import { ProviderChartCode, SummaryReasons, TriageChartCode } from "enums";
import { enumMapper, extractApiErrorMessage, isLastIndex } from "utils";
import { useNavigate, useParams } from "react-router-dom";

import { AdditionalDocumentModal } from "./AdditionalDocumentModal";
import { AddendumsModal } from "./AddendumsModal";
import { useReturnToSelectedChart } from "./useReturnToSelectedChart";
import { ChartTrackingContextProvider, usePermissions } from "contexts";
import {
  SignedSection,
  useDeleteSummaryMiscellaneous,
} from "modules/charts-shared/api/mutations";
import { useState } from "react";
import { useYupValidationResolver } from "hooks";
import { useGetCurrentSummaryFile } from "modules/medical-records/api";

export type SummaryForm = {
  selectedSections: string[];
  miscFilesToAdd: string[];
  selectedProviderCharts: ProviderChartCode[];
  selectedNurseCharts: TriageChartCode[];
};

type SectionItemOption = {
  key: string;
  value: string;
  description: string;
  icon: JSX.Element;
  uploadedFile?: boolean;
};

type SignedSectionItemOption = {
  type: SignedSection;
} & SectionItemOption;

type AdditionalDocumentItemOption = {
  value: string;
  description: string;
};

type SummaryOptions = {
  sectionUploadItems: SignedSectionItemOption[];
  miscFilesToAdd: AdditionalDocumentItemOption[];
};

const PROVIDER_ADDENDUMS_VALUE = "addAddendums";

const SUMMARY_SCHEMA = yup
  .object({
    selectedSections: yup.array(),
    miscFilesToAdd: yup.array(),
    selectedProviderCharts: yup.array(),
    selectedNurseCharts: yup.array(),
  })
  .test(
    "hasSections",
    "Please select at least one element",
    (value) =>
      (value.selectedSections || []).length > 0 ||
      (value.miscFilesToAdd || []).length > 0 ||
      (value.selectedProviderCharts || []).length > 0 ||
      (value.selectedNurseCharts || []).length > 0
  );

export const SUMMARY_SECTION_ITEMS: SectionItemOption[] = [
  {
    key: "addPatientDemographics",
    value: "addPatientDemographics",
    description: "Patient Demographics",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrProfileCircle} />,
  },
  {
    key: "addRegistrationDocuments",
    value: "addRegistrationDocuments",
    description: "Registration Documents",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrKey} />,
  },
  {
    key: "addInsuranceInformation",
    value: "addInsuranceInformation",
    description: "Insurance Information",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrCheckmarkShield} />,
  },
  {
    key: "addNursingChartSummary",
    value: "addNursingChartSummary",
    description: "Nursing Chart Summary",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrNurse} />,
  },
  {
    key: "addProviderChartSummary",
    value: "addProviderChartSummary",
    description: "Provider Chart Summary",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrVitals} />,
  },
  {
    key: "addLabReports",
    value: "addLabReports",
    description: "Lab Reports",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrLabs} />,
  },
  {
    key: "addRadiologyReports",
    value: "addRadiologyReports",
    description: "Radiology Reports",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrRadiology} />,
  },
  {
    key: "addEkgReports",
    value: "addEkgReports",
    description: "Cardio Reports",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrHeartBolt} />,
  },
  {
    key: "addMedicationsReport",
    value: "addMedicationsReport",
    description: "Meds & IV",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrPillsBottle} />,
  },
  {
    key: "AddAllergiesAndMedsReports",
    value: "AddAllergiesAndMedsReports",
    description: "Allergies & Medications Report",
    icon: (
      <Icon h="18px" w="18px" color="gray.700" as={EmrExclamationTriangle} />
    ),
  },
];

const SUMMARY_SECTION_UPLOAD_ITEMS: SignedSectionItemOption[] = [
  {
    key: "addDischargeFile",
    value: "addDischargeFile",
    description: "Discharge Forms - Signed",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrDischarged} />,
    uploadedFile: false,
    type: "Discharge",
  },
  {
    key: "addAmaFile",
    value: "addAmaFile",
    description: "AMA Forms - Signed",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrHand} />,
    uploadedFile: false,
    type: "Ama",
  },
  {
    key: "addTransferFile",
    value: "addTransferFile",
    description: "Transfer Documents - Signed",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrTransportation} />,
    uploadedFile: false,
    type: "Transfer",
  },
  {
    key: "addObservationFile",
    value: "addObservationFile",
    description: "Observation Consent - Signed",
    icon: <Icon h="18px" w="18px" color="gray.700" as={EmrBed} />,
    uploadedFile: false,
    type: "Observation",
  },
];

function Summary() {
  const { encounterId = "" } = useParams<{ encounterId: string }>();

  const [reason, setReason] = React.useState<SummaryReasons | undefined>(
    undefined
  );
  const [otherReason, setOtherReason] = React.useState<string | undefined>(
    undefined
  );

  const toast = useToast();

  const navigate = useNavigate();

  const [sections, setSections] = React.useState<SummaryOptions>({
    sectionUploadItems: [...SUMMARY_SECTION_UPLOAD_ITEMS],
    miscFilesToAdd: [],
  });

  const [mode, setMode] = React.useState<
    "reason" | "selector" | "editor" | "file" | "downloadSelector"
  >("downloadSelector");
  const [file, setFile] = React.useState<Nullable<File | string | Blob>>(null);

  const { data: encounterData, isLoading: isEncounterInfoLoading } =
    useEncounterInfo(encounterId);

  const { data: additionalDocuments } = useSummaryMiscellaneous(
    encounterId || ""
  );

  const { data: signedDocuments } = useSignedSectionsFiles(encounterId || "");

  const yupResolver = useYupValidationResolver(SUMMARY_SCHEMA);

  const form = useForm<SummaryForm>({
    mode: "onSubmit",
    defaultValues: {
      selectedSections: [],
      miscFilesToAdd: [],
      selectedProviderCharts: [],
      selectedNurseCharts: [],
    },
    resolver: yupResolver,
  });

  const {
    handleSubmit,
    formState: { isValid },
    watch,
  } = form;

  const selectedProviderCharts = watch("selectedProviderCharts");
  const selectedNurseCharts = watch("selectedNurseCharts");

  const removeUploadFile = (value: string) => {
    setSections((currentSections) => {
      const changeSection = currentSections.sectionUploadItems.find(
        (sectionfFile) => sectionfFile.value === value
      );
      if (changeSection) changeSection.uploadedFile = false;
      return { ...currentSections };
    });
    const currentValues = form
      .getValues()
      .selectedSections.filter((section) => section !== value);
    form.setValue("selectedSections", currentValues, { shouldValidate: true });
  };

  const removeAdditionalDocument = (value: string) => {
    setSections({
      ...sections,
      miscFilesToAdd: sections.miscFilesToAdd.filter(
        (addDoc) => addDoc.value !== value
      ),
    });
    const currentValues = form
      .getValues()
      .miscFilesToAdd.filter((section) => section !== value);
    form.setValue("miscFilesToAdd", currentValues, {
      shouldValidate: true,
    });
  };

  const { mutateAsync: generateSummaryDocument, isLoading: isLoadingDocument } =
    useGenerateCustomSummaryDocument(encounterId || "", reason, otherReason);

  const onSubmit: SubmitHandler<SummaryForm> = async (data: SummaryForm) => {
    try {
      const payload: UseGenerateCustomSummaryDocumentPayload = {
        addPatientDemographics: false,
        addRegistrationDocuments: false,
        addInsuranceInformation: false,
        addNursingChartSummary: Boolean(data.selectedNurseCharts?.length),
        addProviderChartSummary: Boolean(data.selectedProviderCharts?.length),
        addLabReports: false,
        addRadiologyReports: false,
        addAllergiesMedsReport: false,
        addDischargeFile: false,
        addObservationFile: false,
        addMedicationsReport: false,
        addAddendums: false,
        miscFilesToAdd: data.miscFilesToAdd,
        charts: [
          ...(data.selectedNurseCharts ?? []),
          ...(data.selectedProviderCharts ?? []),
        ],
      };

      (
        data.selectedSections as (keyof Omit<
          UseGenerateCustomSummaryDocumentPayload,
          | "miscFilesToAdd"
          | "charts"
          | "addProviderChartSummary"
          | "addNursingChartSummary"
        >)[]
      ).forEach((key) => {
        payload[key] = true;
      });

      const { data: newFile } = await generateSummaryDocument(payload);
      setFile(newFile);
      setMode("file");
    } catch (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  };

  const onChangeReason = (
    value: SummaryReasons,
    description: string | undefined
  ) => {
    setReason(value);
    setOtherReason(description);
    setMode("selector");
  };

  React.useEffect(() => {
    if (signedDocuments?.data) {
      setSections((currentSections) => {
        return {
          ...currentSections,
          sectionUploadItems: [
            {
              ...currentSections.sectionUploadItems[0],
              uploadedFile: !!signedDocuments.data.hasDischargeFormSigned,
            },
            {
              ...currentSections.sectionUploadItems[1],
              uploadedFile: !!signedDocuments.data.hasAmaFormSigned,
            },
            {
              ...currentSections.sectionUploadItems[2],
              uploadedFile: !!signedDocuments.data.hasTransferDocumentsSigned,
            },
            {
              ...currentSections.sectionUploadItems[3],
              uploadedFile: !!signedDocuments.data.hasObservationConsentSigned,
            },
          ],
        };
      });
    }
  }, [signedDocuments]);

  React.useEffect(() => {
    if (
      !encounterData?.data.hasCurrentSummaryFile &&
      mode === "downloadSelector" &&
      !isEncounterInfoLoading
    ) {
      setMode("reason");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounterData, isEncounterInfoLoading]);

  const { data: progress } = useSummaryProgress(encounterId, isLoadingDocument);

  if (isEncounterInfoLoading) {
    return <Loading />;
  }

  if (mode === "downloadSelector") {
    return (
      <SummarySelectDownload
        isOpen
        onChangeMode={(x) => setMode(x)}
        onSelectFile={(blob) => setFile(blob)}
      />
    );
  }

  if (mode === "reason")
    return (
      <SummaryReasonModal
        isOpen
        onChangeReason={onChangeReason}
        onChangeMode={(x) => setMode(x)}
        hasCurrentSummaryFile={encounterData?.data.hasCurrentSummaryFile}
      />
    );

  if (mode === "selector" || mode === "editor")
    return (
      <Modal isOpen onClose={() => navigate("../")}>
        <ModalOverlay />
        <ModalContent
          zIndex={1000}
          borderRadius="md"
          bgColor="gray.50"
          minHeight="462px"
          width="510px"
          maxWidth="510px"
        >
          <ModalHeader borderTopRadius="md">
            <Flex justifyContent="space-between" alignItems="center">
              <Button
                variant="label"
                color="blue"
                onClick={() => setMode("reason")}
                disabled={isLoadingDocument}
              >
                Cancel
              </Button>
              <Box>Chart Summary Sections</Box>
              <Button
                variant="label"
                color="blue"
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || isLoadingDocument}
                isLoading={isLoadingDocument}
              >
                Generate
              </Button>
            </Flex>
          </ModalHeader>
          <ModalBody>
            {isLoadingDocument &&
            (selectedProviderCharts.length > 0 ||
              selectedNurseCharts.length > 0) ? (
              <Center flexDirection="column" w="100%" py={8} gap="1rem">
                <Icon
                  as={EmrDottedRightArrow}
                  boxSize={16}
                  color="blue"
                  mb={4}
                  style={{ transform: "rotate(90deg)" }}
                />
                <Text fontSize="lg" mb={2} color="blue">
                  {progress?.data?.status ?? "Generating..."}
                </Text>
                <Progress
                  value={parseInt(
                    progress?.data?.percentage?.toString() || "0"
                  )}
                  width="100%"
                  borderRadius="md"
                  mb={2}
                  height="30px"
                  variant="solid"
                />
                <Text fontSize="md">
                  {progress?.data?.percentage ?? 0}% completed
                </Text>
              </Center>
            ) : (
              <FormProvider {...form}>
                <Box height="100%" bgColor="gray.50">
                  <Box height="calc(100% - 80px)">
                    <HStack height="100%" alignItems="start" spacing="0px">
                      <Box overflow="auto" height="100%" width="510px">
                        {mode === "selector" ? (
                          <SectionsSelector
                            form={form}
                            encounterId={encounterId || ""}
                            sectionItems={SUMMARY_SECTION_ITEMS}
                            sectionUploadItems={sections.sectionUploadItems}
                            onEditClick={() => setMode("editor")}
                            additionalDocuments={additionalDocuments?.data}
                          />
                        ) : (
                          <>
                            <SectionsEditor
                              width="510px"
                              mb="15px"
                              encounterId={encounterId || ""}
                              sectionItems={SUMMARY_SECTION_ITEMS}
                              sectionUploadItems={sections.sectionUploadItems}
                              onFinish={() => setMode("selector")}
                              onDelete={(idDoc) => removeUploadFile(idDoc)}
                            />
                            <AdditionalDocumentsEditor
                              additionalDocuments={
                                additionalDocuments?.data || []
                              }
                              encounterId={encounterId || ""}
                              form={form}
                              width="510px"
                              onDelete={(idDoc) =>
                                removeAdditionalDocument(idDoc)
                              }
                            />
                          </>
                        )}
                      </Box>
                    </HStack>
                  </Box>
                </Box>
              </FormProvider>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    );

  return <SummaryFile file={file} />;
}

type SummaryFileProps = {
  file?: Nullable<File | string | Blob>;
};

function SummaryFile({ file }: SummaryFileProps) {
  const navigate = useNavigate();
  const toast = useToast();

  const onClickPrint = React.useCallback(async () => {
    if (!(file instanceof Blob)) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast({ description: "Please allow pop-ups to print this document." });
      return;
    }

    let blobUrl: string | undefined;
    let closeInterval: number | undefined;
    let loadTimeout: number | undefined;
    let cleanedUp = false;

    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      printWindow.removeEventListener("pagehide", cleanup);
      if (closeInterval !== undefined) window.clearInterval(closeInterval);
      if (loadTimeout !== undefined) window.clearTimeout(loadTimeout);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };

    const fail = () => {
      if (cleanedUp) return;
      cleanup();
      if (!printWindow.closed) printWindow.close();
      toast({
        description: "Unable to print this document. Please try again.",
      });
    };

    try {
      printWindow.addEventListener("pagehide", cleanup, { once: true });
      closeInterval = window.setInterval(() => {
        if (printWindow.closed) cleanup();
      }, 500);
      loadTimeout = window.setTimeout(fail, 60_000);

      const header = new Uint8Array(await file.slice(0, 5).arrayBuffer());
      if (cleanedUp) return;
      if (printWindow.closed) {
        cleanup();
        return;
      }
      if (String.fromCharCode(...header) !== "%PDF-") {
        fail();
        return;
      }

      blobUrl = URL.createObjectURL(
        new Blob([file], { type: "application/pdf" })
      );
      const printDocument = printWindow.document;
      printDocument.title = "Medical Records";
      printDocument.body.style.margin = "0";

      const iframe = printDocument.createElement("iframe");
      iframe.title = "Medical Records PDF";
      iframe.style.cssText = "width:100%;height:100vh;border:0";
      iframe.addEventListener(
        "load",
        () => {
          if (cleanedUp) return;
          if (loadTimeout !== undefined) window.clearTimeout(loadTimeout);
          try {
            if (printWindow.closed || !iframe.contentWindow) {
              fail();
              return;
            }
            printWindow.focus();
            iframe.contentWindow.print();
          } catch {
            fail();
          }
        },
        { once: true }
      );
      iframe.addEventListener("error", fail, { once: true });
      iframe.src = blobUrl;
      printDocument.body.appendChild(iframe);
    } catch {
      fail();
    }
  }, [file, toast]);

  return (
    <ChartTrackingContextProvider>
      <Box maxH="100vh" overflow="auto">
        <SummaryPreviewFile
          file={file}
          rightElements={[
            <IconButton
              variant="ghost"
              key="searchButton"
              size="iconSm"
              aria-label="Print"
              height="40px"
              width="40px"
              padding="10px"
              disabled={!file}
              onClick={onClickPrint}
              icon={
                <Icon as={EmrPrinter} color="blue" height="20px" width="20px" />
              }
            />,
          ]}
          middleRightElements={[<PdfZoomControls />]}
          leftElements={[
            <Button
              key="backButton"
              size="iconSm"
              variant="ghost"
              color="blue"
              onClick={() => navigate(-1)}
              leftIcon={<Icon as={SiChevronLeft} />}
            >
              Back
            </Button>,
          ]}
          middleLeftElements={[<PdfViewerSmallPageController />]}
        />
      </Box>
    </ChartTrackingContextProvider>
  );
}

type SectionsSelectorProps = {
  onEditClick?: () => void;
  encounterId: string;
  form: UseFormReturn<SummaryForm>;
  sectionItems: SectionItemOption[];
  sectionUploadItems: SignedSectionItemOption[];
  additionalDocuments?: UseSummaryMiscellaneous;
} & BoxProps;

// Updated SectionsSelector component with tabs
function SectionsSelector(props: SectionsSelectorProps) {
  const {
    onEditClick,
    form,
    encounterId,
    sectionItems,
    sectionUploadItems,
    additionalDocuments,
    ...rest
  } = props;
  const { control, setValue, watch, getValues } = form;
  const [activeTab, setActiveTab] = React.useState(0);

  const { data: activeCharts, isLoading: isActiveChartsLoading } =
    useActiveCharts(encounterId);

  const { data: addendumsData } = useAddendums(encounterId);
  const hasAddendums = (addendumsData?.data.addendums.length ?? 0) > 0;

  // Filter section items by category
  const documentItems = sectionItems.filter(
    (item) => !item.value.includes("Summary")
  );

  const selectedSections = watch("selectedSections");
  const miscFiles = watch("miscFilesToAdd");

  const nonAddendumItemsCount =
    documentItems.length +
    sectionUploadItems.filter((item) => item.uploadedFile).length +
    (additionalDocuments?.length ?? 0);

  const allSelected = React.useMemo(() => {
    return (
      nonAddendumItemsCount + (hasAddendums ? 1 : 0) ===
      selectedSections.length + miscFiles.length
    );
  }, [selectedSections, miscFiles, nonAddendumItemsCount, hasAddendums]);

  const prevHasAddendums = React.useRef(hasAddendums);

  React.useEffect(() => {
    const addendumsAppeared = hasAddendums && !prevHasAddendums.current;
    prevHasAddendums.current = hasAddendums;
    const { selectedSections: sections, miscFilesToAdd } = getValues();

    if (!hasAddendums) {
      if (sections.includes(PROVIDER_ADDENDUMS_VALUE)) {
        setValue(
          "selectedSections",
          sections.filter((section) => section !== PROVIDER_ADDENDUMS_VALUE)
        );
      }
      return;
    }

    if (!addendumsAppeared || nonAddendumItemsCount === 0) return;

    if (
      !sections.includes(PROVIDER_ADDENDUMS_VALUE) &&
      sections.length + miscFilesToAdd.length === nonAddendumItemsCount
    ) {
      setValue("selectedSections", [...sections, PROVIDER_ADDENDUMS_VALUE]);
    }
  }, [hasAddendums, nonAddendumItemsCount, getValues, setValue]);

  const onSelectAll = () => {
    if (allSelected) {
      setValue("selectedSections", []);
      setValue("miscFilesToAdd", []);
    } else {
      setValue("selectedSections", [
        ...documentItems.map((item) => item.value),
        ...sectionUploadItems
          .filter((item) => item.uploadedFile)
          .map((item) => item.value),
        ...(hasAddendums ? [PROVIDER_ADDENDUMS_VALUE] : []),
      ]);
      setValue(
        "miscFilesToAdd",
        additionalDocuments?.map((item) => item.id) ?? []
      );
    }
  };

  const tabs = React.useMemo(() => {
    const TAB_SELECTED_STYLE: CSSObject = {
      bgColor: "white",
      color: "blue",
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.1);",
    };
    return [
      <Tab key="documents" maxH="32px" _selected={TAB_SELECTED_STYLE}>
        Documents
      </Tab>,
      <Tab key="summary" maxH="32px" _selected={TAB_SELECTED_STYLE}>
        Summary
      </Tab>,
    ];
  }, []);

  return (
    <Box {...rest}>
      <ToolbarHeader
        height="0px"
        overflow="hidden"
        alignItems="center"
        rightButtons={[
          <Button key="doneBtn" onClick={() => onEditClick?.()}>
            Edit
          </Button>,
        ]}
      />
      <Card boxShadow="none" border="none" bgColor="transparent">
        <Controller
          name="selectedSections"
          control={control}
          render={({ field }) => (
            <Box>
              <Tabs
                isFitted
                size="sm"
                index={activeTab}
                variant="solid-rounded"
                onChange={(index) => {
                  setActiveTab(index);
                }}
                mb="1.5rem"
              >
                <TabList
                  index={activeTab}
                  maxH="32px"
                  sx={{ "> hr": { display: "none" } }}
                >
                  {tabs}
                </TabList>
                <TabPanels>
                  <TabPanel p={0}>
                    <Box bgColor="white" borderRadius="10px" mb={2}>
                      <SectionItem
                        icon={<Icon as={EmrFolder} />}
                        description={"Select All Documents"}
                        isChecked={allSelected}
                        onClick={onSelectAll}
                      />
                    </Box>

                    <CheckboxListGroup
                      divider={
                        <Divider
                          width="calc(100% - 62px)"
                          marginLeft="62px"
                          height="0px"
                        />
                      }
                      {...field}
                    >
                      {documentItems.map(({ description, icon, value }) => (
                        <CheckboxListItem
                          key={value}
                          value={value}
                          height="52px"
                          p="0"
                          passIsCheckedToChildren
                        >
                          <SectionItem icon={icon} description={description} />
                        </CheckboxListItem>
                      ))}

                      {sectionUploadItems.map(
                        ({ description, icon, value, uploadedFile, type }) => (
                          <CheckboxListItem
                            key={value}
                            value={value}
                            p="0"
                            height="52px"
                            passIsCheckedToChildren
                            isReadOnly={!uploadedFile}
                          >
                            <SignedItem
                              description={description}
                              icon={icon}
                              uploadedFile={uploadedFile}
                              encounterId={encounterId}
                              type={type}
                            />
                          </CheckboxListItem>
                        )
                      )}
                    </CheckboxListGroup>

                    <AdditionalDocumentsSelector
                      additionalDocuments={additionalDocuments || []}
                      encounterId={encounterId || ""}
                      form={form}
                      mt={3}
                    />

                    <ProviderAddendumsHeader encounterId={encounterId}>
                      {hasAddendums && (
                        <CheckboxListGroup {...field}>
                          <CheckboxListItem
                            value={PROVIDER_ADDENDUMS_VALUE}
                            height="52px"
                            p="0"
                            passIsCheckedToChildren
                          >
                            <SectionItem
                              icon={
                                <Icon
                                  h="18px"
                                  w="18px"
                                  color="gray.700"
                                  as={EmrDocument}
                                />
                              }
                              description="Provider Addendums"
                            />
                          </CheckboxListItem>
                        </CheckboxListGroup>
                      )}
                    </ProviderAddendumsHeader>
                  </TabPanel>
                  <TabPanel p={0}>
                    {isActiveChartsLoading && <Loading />}
                    {!isActiveChartsLoading && (
                      <Box>
                        <Text color="gray.650" fontWeight="500">
                          Nursing Chart Summary
                        </Text>
                        <Box bgColor="white" borderRadius="10px" mt={2}>
                          <DividerList dividerLeftMargin="64px">
                            {activeCharts?.data
                              .filter((chart) => chart.chartType === "Triage")
                              .map((chart) => (
                                <SectionItem
                                  key={chart.chartCode}
                                  icon={
                                    <Icon
                                      h="18px"
                                      w="18px"
                                      color="gray.700"
                                      as={EmrNurse}
                                    />
                                  }
                                  description={chart.chartTitle}
                                  isChecked={watch(
                                    "selectedNurseCharts"
                                  ).includes(
                                    chart.chartCode as TriageChartCode
                                  )}
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => {
                                    if (
                                      watch("selectedNurseCharts").includes(
                                        chart.chartCode as TriageChartCode
                                      )
                                    ) {
                                      setValue("selectedNurseCharts", []);
                                    } else {
                                      setValue("selectedNurseCharts", [
                                        chart.chartCode as TriageChartCode,
                                      ]);
                                    }
                                  }}
                                />
                              ))}
                          </DividerList>
                        </Box>

                        <Text color="gray.650" fontWeight="500" mt={6}>
                          Provider Chart Summary
                        </Text>

                        <Box bgColor="white" borderRadius="10px" mt={2}>
                          <DividerList dividerLeftMargin="64px">
                            {activeCharts?.data
                              .filter((chart) => chart.chartType !== "Triage")
                              .map((chart) => (
                                <SectionItem
                                  key={chart.chartCode}
                                  icon={
                                    <Icon
                                      h="18px"
                                      w="18px"
                                      color="gray.700"
                                      as={EmrVitals}
                                    />
                                  }
                                  description={chart.chartTitle}
                                  isChecked={watch(
                                    "selectedProviderCharts"
                                  ).includes(
                                    chart.chartCode as ProviderChartCode
                                  )}
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => {
                                    if (
                                      watch("selectedProviderCharts").includes(
                                        chart.chartCode as ProviderChartCode
                                      )
                                    ) {
                                      setValue("selectedProviderCharts", []);
                                    } else {
                                      setValue("selectedProviderCharts", [
                                        chart.chartCode as ProviderChartCode,
                                      ]);
                                    }
                                  }}
                                />
                              ))}
                          </DividerList>
                        </Box>
                      </Box>
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          )}
        />
      </Card>
    </Box>
  );
}

type SectionsEditorProps = {
  onFinish?: () => void;
  onDelete?: (value: string) => void;
  encounterId: string;
  sectionItems: SectionItemOption[];
  sectionUploadItems: SignedSectionItemOption[];
} & BoxProps;

function SectionsEditor(props: SectionsEditorProps) {
  const {
    onFinish,
    encounterId,
    sectionItems,
    sectionUploadItems,
    onDelete,
    ...rest
  } = props;

  return (
    <Box p="0 15px" {...rest}>
      <ToolbarHeader
        p="0 15px"
        height="60px"
        alignItems="center"
        titleText={
          <chakra.span fontSize="0.9375rem" fontWeight="600">
            Sections
          </chakra.span>
        }
        leftButtons={[
          <Button key="cancelBtn" onClick={() => onFinish?.()}>
            Cancel
          </Button>,
        ]}
        rightButtons={[
          <Button key="doneBtn" onClick={() => onFinish?.()}>
            Done
          </Button>,
        ]}
      />
      <Card boxShadow="none" border="none">
        {sectionItems.map(({ key, description, value }) => (
          <React.Fragment key={key}>
            <SectionItemRemove
              encounterId={encounterId}
              description={description}
              value={value}
            />
            <Divider width="calc(100% - 62px)" marginLeft="62px" height="0px" />
          </React.Fragment>
        ))}

        {sectionUploadItems.map(
          ({ key, description, value, uploadedFile, type }, index) => (
            <React.Fragment key={key}>
              <SectionItemRemove
                encounterId={encounterId}
                description={description}
                value={value}
                uploadedFile={uploadedFile}
                type={type}
                onDeleteClick={(idDoc) => onDelete?.(idDoc)}
              />
              {!isLastIndex(index, sectionUploadItems) && (
                <Divider
                  width="calc(100% - 62px)"
                  marginLeft="62px"
                  height="0px"
                />
              )}
            </React.Fragment>
          )
        )}
      </Card>
    </Box>
  );
}

type AdditionalDocumentsProps = {
  onEditClick?: () => void;
  form: UseFormReturn<SummaryForm>;
  encounterId: string;
  additionalDocuments: {
    id: string;
    name: string;
  }[];
} & BoxProps;

function AdditionalDocumentsSelector(props: AdditionalDocumentsProps) {
  const toast = useToast();
  const { scope } = usePermissions();

  const { onEditClick, form, encounterId, additionalDocuments, ...rest } =
    props;
  const { control } = form;
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [selectedDocument, setSelectedDocument] = React.useState<string | null>(
    null
  );

  const { isLoading: isLoadingDeleteDocument, mutateAsync: deleteDocument } =
    useDeleteSummaryMiscellaneous(encounterId);

  const {
    isOpen: isOpenWarning,
    onClose: onCloseWarning,
    onOpen: onOpenWarning,
  } = useDisclosure();

  const onCloseWarningDialog = () => {
    setSelectedDocument(null);
    onCloseWarning();
  };

  const warningDialogAction = async () => {
    if (selectedDocument) {
      await deleteDocument({
        miscellaneousId: selectedDocument,
      });

      form.setValue(
        "miscFilesToAdd",
        form.getValues().miscFilesToAdd.filter((id) => id !== selectedDocument),
        { shouldValidate: true }
      );

      toast({
        description: "Document deleted successfully",
        status: "success",
      });

      onCloseWarningDialog();
    }
  };

  return (
    <>
      <AdditionalDocumentModal
        isOpen={isOpen}
        onClose={onClose}
        encounterId={encounterId}
      />
      <Box {...rest} backgroundColor="gray.50">
        <ToolbarHeader
          height="60px"
          alignItems="center"
          titleText={
            <chakra.span fontSize="0.9375rem" fontWeight="600">
              Additional Documents
            </chakra.span>
          }
          rightButtons={[
            <IconButton
              key="addBtn"
              variant="ghost"
              size="iconSm"
              aria-label="Add file"
              height="40px"
              width="40px"
              padding="11px"
              icon={
                <Icon as={EmrPlus} color="blue" height="18px" width="18px" />
              }
              onClick={(e) => {
                onOpen();
                e.preventDefault();
              }}
            />,
          ]}
        />
        <Card boxShadow="none" border="none">
          <Controller
            name="miscFilesToAdd"
            control={control}
            render={({ field }) => (
              <CheckboxListGroup
                divider={
                  <Divider
                    width="calc(100% - 62px)"
                    marginLeft="62px"
                    height="0px"
                  />
                }
                {...field}
              >
                {additionalDocuments.map(({ name, id }) => (
                  <CheckboxListItem key={id} value={id} height="52px" p="0">
                    <SectionItem
                      icon={
                        scope("removesummarydocs").isAccessible ? (
                          <Icon
                            h="18px"
                            w="18px"
                            color="red"
                            as={EmrRemove}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();

                              setSelectedDocument(id);
                              onOpenWarning();
                            }}
                          />
                        ) : (
                          <Icon
                            h="18px"
                            w="18px"
                            color="black"
                            as={EmrDocument}
                          />
                        )
                      }
                      isChecked={field.value.includes(id)}
                      description={name}
                    />
                  </CheckboxListItem>
                ))}
              </CheckboxListGroup>
            )}
          />
        </Card>
      </Box>

      <WarningDialog
        isOpen={isOpenWarning}
        onCancel={onCloseWarningDialog}
        title="Warning"
        mainText={"Are you sure you want to update delete this document?"}
        secondaryText={
          "All the information related to this document will be lost."
        }
        onClose={onCloseWarningDialog}
        onAction={warningDialogAction}
        cancelLabel="No"
        actionLabel="Yes"
        blockScrollOnMount={false}
        cancelButtonProps={{ color: "red" }}
        actionButtonProps={{ color: "blue" }}
        isActionLoading={isLoadingDeleteDocument}
      />
    </>
  );
}

type ProviderAddendumsHeaderProps = {
  encounterId: string;
  children?: React.ReactNode;
};

function ProviderAddendumsHeader(props: ProviderAddendumsHeaderProps) {
  const { encounterId, children } = props;
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <>
      <AddendumsModal
        isOpen={isOpen}
        onClose={onClose}
        encounterId={encounterId}
      />
      <Box backgroundColor="gray.50" mt={3}>
        <ToolbarHeader
          height="60px"
          alignItems="center"
          titleText={
            <chakra.span fontSize="0.9375rem" fontWeight="600">
              Provider Addendums
            </chakra.span>
          }
          rightButtons={[
            <IconButton
              key="editAddendumsBtn"
              variant="ghost"
              size="iconSm"
              aria-label="Edit addendums"
              height="40px"
              width="40px"
              padding="11px"
              icon={
                <Icon as={SiPencil} color="blue" height="18px" width="18px" />
              }
              onClick={(e) => {
                onOpen();
                e.preventDefault();
              }}
            />,
          ]}
        />
        {children && (
          <Card boxShadow="none" border="none">
            {children}
          </Card>
        )}
      </Box>
    </>
  );
}

type AdditionalDocumentsEditorProps = {
  onFinish?: () => void;
  onDelete?: (value: string) => void;
  form: UseFormReturn<SummaryForm>;
  encounterId: string;
  additionalDocuments: {
    id: string;
    name: string;
  }[];
} & BoxProps;

function AdditionalDocumentsEditor(props: AdditionalDocumentsEditorProps) {
  const {
    onFinish,
    form,
    encounterId,
    additionalDocuments,
    onDelete,
    ...rest
  } = props;
  const { control } = form;

  return (
    <Box p="0 15px" {...rest}>
      <ToolbarHeader
        p="0 15px"
        height="60px"
        alignItems="center"
        titleText={
          <chakra.span fontSize="0.9375rem" fontWeight="600">
            Additional Documents
          </chakra.span>
        }
        rightButtons={[
          <IconButton
            key="addBtn"
            variant="ghost"
            size="iconSm"
            aria-label="Add file"
            height="40px"
            width="40px"
            padding="11px"
            isDisabled
            icon={
              <Icon as={EmrPlus} color="gray.450" height="18px" width="18px" />
            }
          />,
        ]}
      />
      {additionalDocuments.length > 0 ? (
        <Card boxShadow="none" border="none">
          <Controller
            name="miscFilesToAdd"
            control={control}
            render={({ field }) => (
              <CheckboxListGroup
                divider={
                  <Divider
                    width="calc(100% - 62px)"
                    marginLeft="62px"
                    height="0px"
                  />
                }
                {...field}
              >
                {additionalDocuments.map(({ name, id }) => (
                  <CheckboxListItem key={id} value={id} p="0" height="52px">
                    <DocumentItem
                      encounterId={encounterId}
                      description={name}
                      value={id}
                      fileId={id}
                      onDeleteClick={(idDoc) => onDelete?.(idDoc)}
                    />
                  </CheckboxListItem>
                ))}
              </CheckboxListGroup>
            )}
          />
        </Card>
      ) : (
        <Box
          textAlign="center"
          fontSize="0.9375rem"
          fontWeight="600"
          color="gray.700"
        >
          No other documents have been added
        </Box>
      )}
    </Box>
  );
}

type SummaryPreviewFileProps = {
  file?: Nullable<File | string | Blob>;
  leftElements?: React.ReactNode[] | undefined;
  rightElements?: React.ReactNode[] | undefined;
  middleRightElements?: React.ReactNode[] | undefined;
  middleLeftElements?: React.ReactNode[] | undefined;
};

function SummaryPreviewFile(props: SummaryPreviewFileProps) {
  const {
    file = null,
    leftElements,
    rightElements,
    middleLeftElements,
    middleRightElements,
  } = props;
  return (
    <PdfViewer value={file}>
      <PdfViewerContainer
        contentContainerProps={{ maxH: "92vh" }}
        header={
          <PdfViewerHeader
            title="Chart Summary"
            leftElements={leftElements}
            rightElements={rightElements}
            middleLeftElements={middleLeftElements}
            middleRightElements={middleRightElements}
          />
        }
        activePage={<PdfViewerActivePage />}
        preview={<PdfViewerPreview />}
      />
    </PdfViewer>
  );
}

type SummaryReasonModalProps = {
  isOpen: boolean;
  onChangeReason: (
    reason: SummaryReasons,
    description: string | undefined
  ) => void;
  onChangeMode: (mode: "downloadSelector") => void;
  hasCurrentSummaryFile?: boolean;
};

function SummaryReasonModal({
  isOpen,
  onChangeReason,
  onChangeMode,
  hasCurrentSummaryFile,
}: SummaryReasonModalProps) {
  const returnToSelectedChart = useReturnToSelectedChart();

  const [selectedReason, setSelectedReason] = useState<
    SummaryReasons | undefined
  >(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);

  const reasons = enumMapper.getOptions("summaryReasons");

  const warningDisclosure = useDisclosure();

  const reasonsFiltered = reasons.filter(
    (reason) => reason.value !== "SummaryReasonOther"
  );

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered>
      <ModalOverlay />
      <ModalContent
        zIndex={1000}
        borderRadius="md"
        bgColor="gray.50"
        minHeight="480px"
        width="510px"
        maxWidth="510px"
      >
        <ModalHeader borderTopRadius="md">
          <Flex justifyContent="space-between" alignItems="center">
            <Button
              variant="label"
              color="blue"
              onClick={() => {
                if (hasCurrentSummaryFile) {
                  onChangeMode("downloadSelector");
                } else {
                  returnToSelectedChart();
                }
              }}
            >
              Cancel
            </Button>
            <Box>Medical Records</Box>
            <Button
              variant="label"
              color="blue"
              onClick={() => {
                if (
                  selectedReason === "SummaryReasonOther" &&
                  (description?.trim().length ?? 0) < 6
                ) {
                  warningDisclosure.onOpen();
                  return;
                }

                if (selectedReason) {
                  onChangeReason(selectedReason, description);
                }
              }}
              disabled={!selectedReason}
            >
              Continue
            </Button>
            <WarningDialog
              isOpen={warningDisclosure.isOpen}
              mainText="You must enter a reason to continue."
              title="Warning!"
              actionLabel="OK"
              onlyActionButton
              actionButtonProps={{ color: "blue" }}
              onCancel={warningDisclosure.onClose}
              onAction={warningDisclosure.onClose}
              onClose={warningDisclosure.onClose}
            />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Box textAlign="center" pb={6}>
            Select the Reason you are accessing these <br /> Medical Records.
          </Box>
          <Box
            borderRadius="10px"
            bg="white"
            overflow="hidden"
            outline="none"
            minH="50px"
            mb={2}
          >
            {reasonsFiltered.map((item, index) => (
              <>
                <Box
                  display="flex"
                  px={4}
                  py={4}
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => {
                    setSelectedReason(item.value);
                  }}
                  cursor="pointer"
                >
                  <Box fontWeight="600" pl={2}>
                    {item.label}
                  </Box>
                  {selectedReason === item.value && (
                    <Box h="24px" w="24px" pt={1}>
                      <Icon
                        h="14px"
                        w="14px"
                        as={EmrCheck}
                        color="blue"
                        verticalAlign="top"
                      />
                    </Box>
                  )}
                </Box>
                {!isLastIndex(index, reasonsFiltered) && (
                  <Divider
                    width={`calc(100% - 20px)`}
                    ml="20px"
                    mt={1}
                    h="0.5px"
                  />
                )}
              </>
            ))}
          </Box>
          {selectedReason === "SummaryReasonOther" && (
            <Box
              borderRadius="10px"
              bg="white"
              overflow="hidden"
              outline="none"
              minH="50px"
              p={1}
              mt={2}
              mb={2}
            >
              <Textarea
                placeholder="Type other reason here..."
                size="sm"
                resize="none"
                onChange={(e) => {
                  setDescription(e.currentTarget.value);
                }}
              />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

type SummarySelectDownloadProps = {
  isOpen: boolean;
  onChangeMode: (mode: "reason" | "file") => void;
  onSelectFile: (blob: Blob) => void;
};

function SummarySelectDownload({
  isOpen,
  onSelectFile,
  onChangeMode,
}: SummarySelectDownloadProps) {
  const returnToSelectedChart = useReturnToSelectedChart();
  const { encounterId = "" } = useParams<ChartRouteBaseParams>();

  const toast = useToast();

  const [enabled, setEnabled] = React.useState(false);

  const { data, isLoading, isFetching, refetch, error } =
    useGetCurrentSummaryFile(encounterId, { enabled });

  React.useEffect(() => {
    if (!!data?.data && enabled && !isLoading && !isFetching) {
      const file = data?.data;
      onSelectFile(file);
      setEnabled(false);
      onChangeMode("file");
    }
  }, [data, enabled, isLoading, isFetching, onSelectFile, onChangeMode]);

  React.useEffect(() => {
    if (error) {
      toast({ description: "Summary not available" });
      setEnabled(false);
    }
  }, [error, toast]);

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered>
      <ModalOverlay />
      <ModalContent
        zIndex={1000}
        borderRadius="md"
        bgColor="gray.50"
        minHeight="480px"
        width="510px"
        maxWidth="510px"
      >
        <ModalHeader borderTopRadius="md">
          <Flex justifyContent="space-between" alignItems="center">
            <Button
              variant="label"
              color="blue"
              onClick={() => {
                returnToSelectedChart();
              }}
            >
              Cancel
            </Button>
            <Box>Medical Records</Box>
            <Box />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex
            flexDir="column"
            gap={3}
            justifyContent="center"
            alignItems="center"
          >
            <Text>Would you like to:</Text>
            <Text textAlign="center">
              View the patient's full Medical Record for this encounter that is
              already saved on the system database?
            </Text>
            <Button
              width="100%"
              margin="auto"
              onClick={() => {
                refetch();
                if (!enabled) setEnabled(true);
              }}
              isLoading={isLoading || isFetching}
            >
              View Saved Medical Record
            </Button>

            <Text>Or:</Text>
            <Text textAlign="center">
              Generate a new summary with the ability to select specific
              sections of the patient encounter?
            </Text>
            <Button
              width="100%"
              margin="auto"
              bgColor="white"
              color="blue"
              border="1px solid blue"
              onClick={() => onChangeMode("reason")}
            >
              Generate Custom Summary
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export { Summary };
